import { useCallback, useMemo } from 'react';
import { useSandboxSession } from '../SandboxSessionContext';
import { getDraftTitle } from '../utils/draftPreviewPresentation';

const BRANCH_READ_ONLY = 'Actions are unavailable in this preview.';
export const BRANCH_NOT_LATEST = 'Open the latest changes to run Actions on this branch.';

/**
 * The OS Branch an Action pressed in branch preview runs on, or null outside branch preview.
 *
 * A person can act on the current state of a branch opened from a chat or a review.
 * `onChanged` moves the preview to the branch's new working revision after an
 * Action has changed it, resolving once the branch list has refreshed.
 */
export const useBranchActionTarget = () => {
  const { isPreviewActive, selectedSandbox, sandboxContext, previewSandboxHints, clearPreviewHints, refetchSandboxes } = useSandboxSession();
  // The refreshed branch list carries the new working revision; the preview reopens on it.
  const onChanged = useCallback(async () => {
    clearPreviewHints?.();
    await refetchSandboxes?.();
  }, [clearPreviewHints, refetchSandboxes]);
  return useMemo(() => {
    const id = sandboxContext?.sandbox_id;
    if (!isPreviewActive || !id) return null;
    const revisionId = sandboxContext.active_overlay_id || null;
    let unavailableReason = null;
    if (!['chat', 'approval'].includes(selectedSandbox?.preview_access)) unavailableReason = BRANCH_READ_ONLY;
    else if (revisionId !== (selectedSandbox.working_overlay_id || selectedSandbox.active_overlay_id || null)) unavailableReason = BRANCH_NOT_LATEST;
    return {
      id: String(id),
      revisionId,
      approvalId: previewSandboxHints?.approval_id || selectedSandbox?.publication?.approval_id,
      name: getDraftTitle(selectedSandbox || sandboxContext),
      unavailableReason,
      onChanged,
    };
  }, [isPreviewActive, sandboxContext, previewSandboxHints?.approval_id, selectedSandbox, onChanged]);
};

export default useBranchActionTarget;
